import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import rainbow from "../../assets/lineaRainbowCarrear.svg";
import { JobApiItem, useJobs } from "../../hooks/useJobs";
import { ToggleChip } from "./ToggleChip";

export const AvailableJobs: React.FC = () => {
  const { jobs, loading, error } = useJobs();
  const [selectedDept, setSelectedDept] = useState<string>("Todos");

  const normalized = useMemo<JobApiItem[]>(() => {
    return jobs.map((item: JobApiItem) => {
      return item;
    });
  }, [jobs]);

  const departments = useMemo<string[]>(() => {
    const set = new Set<string>(["Todos"]);
    normalized.forEach((j) => set.add(j.jobDepartment || "Others"));
    return Array.from(set);
  }, [normalized]);

  const filtered = useMemo<JobApiItem[]>(() => {
    if (selectedDept === "Todos") return normalized;
    return normalized.filter(
      (j) => (j.jobDepartment || "Others") === selectedDept
    );
  }, [normalized, selectedDept]);

  return (
    <div className="w-full min-h-screen bg-white px-6 sm:px-10 md:px-16 lg:px-24 text-black pt-20">
      <div className="max-w-6xl mx-auto text-center">
        <h1 className="text-2xl md:text-4xl font-bold tracking-tight uppercase mt-10">
          EMPLEOS
        </h1>
        <p className="text-black/80 mt-2 md:mt-3 text-sm md:text-base">
          Echa un vistazo a algunas de las
          <br />
          vacantes actuales.
        </p>
        <div className="flex justify-center mt-4 mb-6">
          <img
            draggable={false}
            src={rainbow}
            alt="decoración"
            className="w-20 md:w-28"
          />
        </div>

        {/* Department toggles */}
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          {departments.map((dept) => (
            <ToggleChip
              key={dept}
              label={dept}
              isActive={selectedDept === dept}
              onClick={() => setSelectedDept(dept)}
            />
          ))}
        </div>
      </div>

      {/* Jobs grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {loading && (
          <div className="col-span-full text-center text-black/60">
            No hay vacantes disponibles...
          </div>
        )}
        {error && (
          <div className="col-span-full text-center text-red-600">
            Error al cargar las vacantes...
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="col-span-full text-center text-black/60">
            No hay vacantes disponibles...
          </div>
        )}
        {filtered.map((job, index) => (
          <Link
            to={`/details/${job.documentId}`}
            key={job.documentId}
            className="group flex items-start gap-4 border border-black/10 hover:border-black/30 rounded-md p-4 transition-colors duration-200 text-left"
          >
            <div className="text-black/60 text-left group-hover:text-black/80 font-mono min-w-[2ch]">
              {(index + 1).toString().padStart(2, "0")}
            </div>
            <div className="flex-1">
              <h3 className="text-black font-semibold text-lg md:text-xl leading-snug text-left">
                {job.jobTitle}
              </h3>
              <p className="text-black/70 text-sm md:text-base mt-1 text-left ">
                {job.jobCountry} — {job.jobModality}
              </p>
              <p className="text-black/60 text-xs mt-1 text-left">
                {job.jobDepartment}
              </p>
            </div>
          </Link>
        ))}
      </div>

      <div className="pb-40" />
    </div>
  );
};

export default AvailableJobs;
